/**
 * WHAT A CURRENCY CODE MEANS, AND WHICH ONES THIS SCREEN HAS SHOWN AS MONEY.
 *
 * Two small facts the currency hover card (CurrencyHover.tsx) needs, kept apart
 * from React so the formatters in ui.tsx can record into it without importing a
 * component.
 *
 * ---------------------------------------------------------------------------
 * THE REGISTRY: ONLY WHAT money() AND compact() ACTUALLY FORMATTED
 *
 * The hover card recognises a figure by its SHAPE — "VND 4,939,243,600" — so it
 * works on every screen without every call site being rewritten. A shape alone
 * is not enough to be honest, though: ISO 4217 includes TOP, ALL, CUP and MOP,
 * and a product cell reading "CUP 250" would otherwise be labelled Tongan
 * pa'anga. So the card only names a code the formatters have put on screen as
 * money in this session. Registration is idempotent, so a render that runs twice
 * (StrictMode, a re-render) records nothing new.
 *
 * ---------------------------------------------------------------------------
 * THE NAMES COME FROM THE BROWSER, NOT A TABLE WE MAINTAIN
 *
 * Intl.DisplayNames carries CLDR's names for every ISO 4217 currency and every
 * ISO 3166 region, so a currency this platform has never shown still reads
 * correctly the day it first appears. Measured on the clone 17 Sep 2026: 26
 * distinct codes across the four tables the reports read, every one valid ISO.
 *
 * The COUNTRY is derived: an ISO 4217 code begins with its issuer's ISO 3166
 * code (VND is VN, SAR is SA), with two classes of exception handled below —
 * EUR, whose issuer is the European Union, and the X-codes (XAF, XOF, XAU, XDR),
 * which are supranational or commodity units with no single country to name.
 */

const formatted = new Set<string>()

/** Called by money() and compact() for the unit they render: a code, or USD for "$". */
export function noteCurrency(code: string): void {
  formatted.add(code)
}

/** Has a formatter shown this code as money on this screen? */
export function wasFormatted(code: string): boolean {
  return formatted.has(code)
}

export interface CurrencyInfo {
  code: string
  /** ISO 3166 region whose flag to show, or null when no single country issues it. */
  region: string | null
  /** "Vietnam" — null when there is no single country to name. */
  country: string | null
  /** "Vietnamese Dong" — null for a code the browser does not recognise. */
  name: string | null
}

/* Issuers that are not the first two letters of the code. */
const ISSUER: Record<string, string> = { EUR: 'EU' }

let names: { currency: Intl.DisplayNames; region: Intl.DisplayNames } | null | undefined

function displayNames() {
  if (names !== undefined) return names
  try {
    names = {
      currency: new Intl.DisplayNames(['en'], { type: 'currency', fallback: 'none' }),
      region: new Intl.DisplayNames(['en'], { type: 'region', fallback: 'none' }),
    }
  } catch {
    // A browser without Intl.DisplayNames gets no card rather than a wrong one.
    names = null
  }
  return names
}

const cache = new Map<string, CurrencyInfo>()

export function currencyInfo(code: string): CurrencyInfo {
  const hit = cache.get(code)
  if (hit) return hit

  const dn = displayNames()
  const region = ISSUER[code] ?? (/^[A-WYZ][A-Z]{2}$/.test(code) ? code.slice(0, 2) : null)

  let country: string | null = null
  if (dn && region) {
    const n = dn.region.of(region)
    // `fallback: 'none'` is not enough on its own: CLDR has a real entry for the
    // unknown region, so an unassigned code comes back as "Unknown Region"
    // rather than undefined. Measured under ICU 78.
    country = n && n !== region && n !== 'Unknown Region' ? n : null
  }

  let name: string | null = null
  if (dn && /^[A-Z]{3}$/.test(code)) {
    const n = dn.currency.of(code)
    name = n && n !== code ? n : null
  }

  const info: CurrencyInfo = { code, region: country ? region : null, country, name }
  cache.set(code, info)
  return info
}
